import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiNavigation, FiFileText, FiLoader, FiMessageCircle, FiX } = FiIcons;

const ShippingCalculator = () => {
  const [originQuery, setOriginQuery] = useState('');
  const [destQuery, setDestQuery] = useState('');
  const [originResults, setOriginResults] = useState([]);
  const [destResults, setDestResults] = useState([]);
  const [selectedOrigin, setSelectedOrigin] = useState(null);
  const [selectedDest, setSelectedDest] = useState(null);
  const [weight, setWeight] = useState('');
  const [cargoType, setCargoType] = useState('General Cargo');
  const [isCalculating, setIsCalculating] = useState(false);
  const [price, setPrice] = useState(null);
  const [distance, setDistance] = useState(null);

  // Form Order Data
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    nama: '',
    nomorWA: '',
    alamat: ''
  });

  const fetchLocations = async (query) => {
    try {
      const res = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}&count=5&language=id&format=json`);
      const data = await res.json();
      return data.results || [];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const fullName = selectedOrigin ? `${selectedOrigin.name}${selectedOrigin.admin1 ? `, ${selectedOrigin.admin1}` : ''}, ${selectedOrigin.country}` : '';
      if (originQuery.length >= 3 && originQuery !== fullName) {
        fetchLocations(originQuery).then(setOriginResults);
      } else {
        setOriginResults([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [originQuery, selectedOrigin]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const fullName = selectedDest ? `${selectedDest.name}${selectedDest.admin1 ? `, ${selectedDest.admin1}` : ''}, ${selectedDest.country}` : '';
      if (destQuery.length >= 3 && destQuery !== fullName) {
        fetchLocations(destQuery).then(setDestResults);
      } else {
        setDestResults([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [destQuery, selectedDest]);

  const handleSelectOrigin = (loc) => {
    setSelectedOrigin(loc);
    setOriginQuery(`${loc.name}${loc.admin1 ? `, ${loc.admin1}` : ''}, ${loc.country}`);
    setOriginResults([]);
    setPrice(null);
  };

  const handleSelectDest = (loc) => {
    setSelectedDest(loc);
    setDestQuery(`${loc.name}${loc.admin1 ? `, ${loc.admin1}` : ''}, ${loc.country}`);
    setDestResults([]);
    setPrice(null);
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const handleCalculate = () => {
    if (!selectedOrigin || !selectedDest || !weight) return;
    
    setIsCalculating(true);
    setPrice(null);
    
    setTimeout(() => {
      const dist = calculateDistance(
        selectedOrigin.latitude, selectedOrigin.longitude,
        selectedDest.latitude, selectedDest.longitude
      );
      
      let multiplier = 1;
      if (['Heavy Equipment', 'Agricultural Machinery', 'Industrial Machines'].includes(cargoType)) multiplier = 1.8;
      if (cargoType === 'Project Cargo') multiplier = 2.0;
      if (cargoType === 'Commercial Goods') multiplier = 1.2;

      const calcPrice = Math.max(dist, 50) * Math.max(parseFloat(weight), 10) * 150 * multiplier;
      
      setPrice(calcPrice);
      setDistance(dist);
      setIsCalculating(false);
    }, 1200);
  };

  const handleSendWA = () => {
    if (!formData.nama || !formData.nomorWA || !formData.alamat) {
      alert("Mohon lengkapi Nama, Nomor WA, dan Alamat Anda.");
      return;
    }

    const formattedPrice = new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(price);
    
    const text = `Halo Nusantara Logistik, saya ingin memesan pengiriman:\n\n*Data Pemesan:*\nNama: ${formData.nama}\nNo WA: ${formData.nomorWA}\nAlamat: ${formData.alamat}\n\n*Detail Kargo:*\nAsal: ${selectedOrigin?.name}\nTujuan: ${selectedDest?.name}\nBerat: ${weight} Kg\nTipe Kargo: ${cargoType}\nEstimasi Harga: ${formattedPrice}\n\nMohon info untuk proses selanjutnya.`;
    
    const encodedText = encodeURIComponent(text);
    // Diperbarui sesuai permintaan nomor WhatsApp
    const waUrl = `https://wa.me/628211317248?text=${encodedText}`;
    
    const link = document.createElement('a');
    link.href = waUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setIsFormOpen(false);
  };

  return (
    <>
      <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); handleCalculate(); }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="relative">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Titik Asal</label>
            <div className="relative">
              <SafeIcon icon={FiMapPin} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input 
                type="text" 
                value={originQuery}
                onChange={(e) => {
                  setOriginQuery(e.target.value);
                  if(selectedOrigin) setSelectedOrigin(null);
                }}
                placeholder="Ketik Kota Asal..."
                className="w-full bg-slate-50 border border-slate-200 rounded-lg py-3 pl-10 pr-3 text-sm focus:outline-none focus:border-orange focus:ring-1 focus:ring-orange transition-all"
                required
              />
            </div>
            {originResults.length > 0 && (
              <ul className="absolute z-50 w-full bg-white mt-1 border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {originResults.map(loc => (
                  <li 
                    key={loc.id} 
                    onClick={() => handleSelectOrigin(loc)}
                    className="px-4 py-2 hover:bg-slate-50 cursor-pointer text-sm text-slate-700 border-b border-slate-100 last:border-0"
                  >
                    <div className="font-semibold">{loc.name}</div>
                    <div className="text-xs text-slate-500">{loc.admin1 ? `${loc.admin1}, ` : ''}{loc.country}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="relative">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Tujuan</label>
            <div className="relative">
              <SafeIcon icon={FiNavigation} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input 
                type="text" 
                value={destQuery}
                onChange={(e) => {
                  setDestQuery(e.target.value);
                  if(selectedDest) setSelectedDest(null);
                }}
                placeholder="Ketik Kota Tujuan..."
                className="w-full bg-slate-50 border border-slate-200 rounded-lg py-3 pl-10 pr-3 text-sm focus:outline-none focus:border-orange focus:ring-1 focus:ring-orange transition-all"
                required
              />
            </div>
            {destResults.length > 0 && (
              <ul className="absolute z-50 w-full bg-white mt-1 border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {destResults.map(loc => (
                  <li 
                    key={loc.id} 
                    onClick={() => handleSelectDest(loc)}
                    className="px-4 py-2 hover:bg-slate-50 cursor-pointer text-sm text-slate-700 border-b border-slate-100 last:border-0"
                  >
                    <div className="font-semibold">{loc.name}</div>
                    <div className="text-xs text-slate-500">{loc.admin1 ? `${loc.admin1}, ` : ''}{loc.country}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Berat Total (Kg)</label>
            <input 
              type="number" 
              value={weight}
              onChange={(e) => { setWeight(e.target.value); setPrice(null); }}
              placeholder="0.00"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg py-3 px-4 text-sm focus:outline-none focus:border-orange focus:ring-1 focus:ring-orange transition-all"
              required
              min="1"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Tipe Kargo</label>
            <select 
              value={cargoType}
              onChange={(e) => { setCargoType(e.target.value); setPrice(null); }}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg py-3 px-4 text-sm focus:outline-none focus:border-orange focus:ring-1 focus:ring-orange transition-all text-slate-600"
            >
              <option>General Cargo</option>
              <option>Heavy Equipment</option>
              <option>Agricultural Machinery</option>
              <option>Industrial Machines</option>
              <option>Project Cargo</option>
              <option>Commercial Goods</option>
              <option>Personal & Business Shipments</option>
            </select>
          </div>
        </div>

        <AnimatePresence>
          {price !== null && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-orange/10 border border-orange/20 rounded-xl p-4 mt-4 overflow-hidden"
            >
              <div className="text-sm text-slate-600 mb-1 font-medium">Estimasi Harga Pengiriman</div>
              <div className="text-2xl font-bold text-navy tracking-tight mt-1">
                {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(price)}
              </div>
              <div className="text-xs text-slate-500 mt-2">
                *Jarak estimasi: {distance?.toFixed(1)} km
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {price !== null ? (
          <button 
            type="button"
            onClick={() => setIsFormOpen(true)}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-4 rounded-xl mt-4 transition-colors flex justify-center items-center gap-2"
          >
            Kirim Sekarang <SafeIcon icon={FiMessageCircle} />
          </button>
        ) : (
          <button 
            type="submit" 
            disabled={isCalculating || !selectedOrigin || !selectedDest || !weight}
            className="w-full bg-navy hover:bg-navy-light disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-xl mt-4 transition-colors flex justify-center items-center gap-2"
          >
            {isCalculating ? (
              <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
                <SafeIcon icon={FiLoader} className="text-xl" />
              </motion.div>
            ) : (
              <><SafeIcon icon={FiFileText} /> Hitung Estimasi</>
            )}
          </button>
        )}
      </form>

      {/* Popup Form Data Diri */}
      <AnimatePresence>
        {isFormOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-navy/80 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.95 }}
              className="bg-white rounded-3xl p-6 md:p-8 w-full max-w-lg shadow-2xl relative max-h-[90vh] overflow-y-auto"
            >
              <button 
                onClick={() => setIsFormOpen(false)}
                className="absolute top-6 right-6 text-slate-400 hover:text-navy transition-colors"
              >
                <SafeIcon icon={FiX} className="text-2xl" />
              </button>
              
              <h3 className="text-2xl font-bold text-navy mb-6 pr-8">Lengkapi Data Pesanan</h3>
              
              {/* Prepopulated Data */}
              <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl mb-6 space-y-2 text-sm text-slate-600">
                <div className="flex justify-between border-b border-slate-200 pb-2">
                  <span className="font-semibold text-slate-500">Rute:</span>
                  <span className="text-right">{selectedOrigin?.name} &rarr; {selectedDest?.name}</span>
                </div>
                <div className="flex justify-between border-b border-slate-200 pb-2 pt-1">
                  <span className="font-semibold text-slate-500">Kargo:</span>
                  <span className="text-right">{weight} Kg ({cargoType})</span>
                </div>
                <div className="flex justify-between pt-1">
                  <span className="font-semibold text-slate-500">Estimasi:</span>
                  <span className="font-bold text-orange">
                    {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(price)}
                  </span>
                </div>
              </div>

              {/* Input Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Nama Lengkap</label>
                  <input 
                    type="text" 
                    value={formData.nama}
                    onChange={(e) => setFormData({...formData, nama: e.target.value})}
                    placeholder="Masukkan nama Anda"
                    className="w-full bg-white border border-slate-200 rounded-lg py-3 px-4 text-sm focus:outline-none focus:border-orange focus:ring-1 focus:ring-orange transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Nomor WhatsApp</label>
                  <input 
                    type="tel" 
                    value={formData.nomorWA}
                    onChange={(e) => setFormData({...formData, nomorWA: e.target.value})}
                    placeholder="Contoh: 08123456789"
                    className="w-full bg-white border border-slate-200 rounded-lg py-3 px-4 text-sm focus:outline-none focus:border-orange focus:ring-1 focus:ring-orange transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Alamat Lengkap</label>
                  <textarea 
                    value={formData.alamat}
                    onChange={(e) => setFormData({...formData, alamat: e.target.value})}
                    placeholder="Detail alamat jemput/tujuan"
                    rows="3"
                    className="w-full bg-white border border-slate-200 rounded-lg py-3 px-4 text-sm focus:outline-none focus:border-orange focus:ring-1 focus:ring-orange transition-all resize-none"
                  ></textarea>
                </div>
              </div>

              <button 
                onClick={handleSendWA}
                className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-bold py-4 rounded-xl mt-6 transition-colors flex justify-center items-center gap-2"
              >
                Kirim Pesanan <SafeIcon icon={FiMessageCircle} className="text-xl" />
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ShippingCalculator;